import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import dataService from "features/Application/services/dataServices/typedDataService";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import { type DistributedOpUpdateParams } from "interfaces";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  type AvailableDomainsConfiguration,
  type AvailableDomainsPoolsConfiguration,
} from "features/Library/SimulatedPhishing/Domains/types/state";
import axios from "axios";
import { type ResponseError } from "types";
import { getPrefix } from "../../../Application/slices/models";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "../../../../enums/reducer";

export const addDomainToAccount = (accountIds: number[], onCompleted?: () => void) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const domain = getState().accounts.availableDomains.selectedDomain;

    if (!domain.isAssigned) return;

    const hasDomain = domain.domainId != null;
    const hasPool = domain.domainPoolId != null;
    if (hasDomain === hasPool) {
      throw new Error("Exactly one of domainId or domainPoolId must be provided.");
    }
    const kindLabel = hasDomain ? "Domain" : "Domain Pool";
    const updateParams: DistributedOpUpdateParams = {
      id: "AddDomainToAccount",
      title: `Add ${kindLabel} to account`,
      getOperationProps: async () => {
        const result = await dataService.threatDefence.domains.addDomainToAccountsAsync({
          domainId: domain.domainId,
          domainPoolId: domain.domainPoolId,
          accountIds,
        });
        return {
          operationId: result.operationId,
          stepsIds: result.stepsIds,
          statusUrl: result.statusUrl,
        };
      },
      successTransientMessage: `${kindLabel} has been added to account successfully`,
      failureTransientMessage: `Adding ${kindLabel} to account failed!`,
      onCompleted,
    };
    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };
};

export const fetchAccountAvailableDomains = createAsyncThunk<
  AvailableDomainsConfiguration,
  number,
  { rejectValue: ResponseError }
>(
  getPrefix({
    namespace: ReducerNamespaceTypes.Accounts,
    entity: ReducerEntityPrefixTypes.ThreatDefence,
    name: "availableDomains",
  }),
  async (accountId: number, { rejectWithValue }) => {
    try {
      return await dataService.threatDefence.domains.getAccountAvailableDomainsAsync(accountId);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data as ResponseError);
      }

      return rejectWithValue({
        message: error.message || "Unknown error occurred",
        status: 500,
      });
    }
  },
);

export const fetchAccountAvailableDomainsPools = createAsyncThunk<AvailableDomainsPoolsConfiguration>(
  getPrefix({
    namespace: ReducerNamespaceTypes.Accounts,
    entity: ReducerEntityPrefixTypes.ThreatDefence,
    name: "availableDomainsPools",
  }),
  () => {
    return dataService.threatDefence.domains.getAccountAvailableDomainPoolsAsync();
  },
);
